import { ArrowForward, DeleteOutlined } from '@mui/icons-material'
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import './SerialNumbers.css'
import { serialNumberType } from '../types'
import { useState } from 'react'
import axios from 'axios'

function SerialNumbers(props: {
  order: { customerName: string; jobName: string; itemName: string; salesOrderNumber: string }
  serialNumbers: serialNumberType[]
  orderId: string
  lineId: string
  userType: string
  userId: string
  setOpen: (open: boolean) => void
  setParentSerialNumbers: (serialNumbers: serialNumberType[]) => void
}) {
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<string[]>([])
  const [serialNumbers, setSerialNumbers] = useState<string[]>(props.serialNumbers.map(s => s.serial_number))
  const [newSerialNumbers, setNewSerialNumbers] = useState<Set<string>>(new Set())
  const [newSerialInput, setNewSerialInput] = useState<string>('')
  const [addError, setAddError] = useState<{ show: boolean; duplicates?: string[] }>({ show: false })
  const [deleteError, setDeleteError] = useState<{ show: boolean }>({ show: false })

  const handleAdd = () => {
    axios
      .post(`/salesOrder/${props.lineId}/addSerialNumbers`, {
        orderId: props.orderId,
        serialNumbers: Array.from(newSerialNumbers),
        userId: props.userId,
      })
      .then(() => {
        setSerialNumbers([...serialNumbers, ...newSerialNumbers])
        props.setParentSerialNumbers([
          ...props.serialNumbers,
          ...Array.from(newSerialNumbers).map(s => ({
            sales_order_txn_id: props.orderId,
            sales_order_line_txn_id: props.lineId,
            serial_number: s.trim(),
          })),
        ])
        setNewSerialNumbers(new Set())
        setAddError({ show: false })
      })
      .catch(err => {
        // case - duplicate serial number
        console.error('Error response:', err.response?.data)
        if (err.response?.status === 400 && err.response?.data?.includes('Serial number already exists')) {
          const duplicates = Array.from(newSerialNumbers).filter(sn => serialNumbers.includes(sn))
          setAddError({ show: true, duplicates })
        } else {
          // case - random error
          setAddError({ show: true })
        }
      })
  }
  const handleDelete = () => {
    axios
      .delete(`/salesOrder/${props.lineId}/deleteSerialNumbers`, {
        data: {
          serialNumbers: selectedSerialNumbers,
          userId: props.userId,
          orderId: props.orderId,
        },
      })
      .then(() => {
        setSelectedSerialNumbers([])
        setSerialNumbers(serialNumbers.filter(s => !selectedSerialNumbers.includes(s)))
        props.setParentSerialNumbers(props.serialNumbers.filter(s => !selectedSerialNumbers.includes(s.serial_number)))
        setDeleteError({ show: false })
      })
      .catch(err => {
        console.error(err)
        setDeleteError({ show: true })
      })
  }

  return (
    <div className="serial-numbers-container">
      <div className="serial-numbers-header">
        <div>
          <Typography variant="h6">Serial Numbers</Typography>
          <IconButton onClick={() => props.setOpen(false)}>
            <ArrowForward />
          </IconButton>
        </div>
        <Divider />
      </div>
      <div className="serial-numbers-body">
        <div className="serial-numbers-body-order-info">
          <div>
            <Typography variant="body2" color="textSecondary">
              Customer Name:
            </Typography>
            <Typography variant="body1">{props.order.customerName}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Item Name:
            </Typography>
            <Typography variant="body1">{props.order.itemName}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Job Name:
            </Typography>
            <Typography variant="body1">{props.order.jobName}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Sales Order Number:
            </Typography>
            <Typography variant="body1">{props.order.salesOrderNumber}</Typography>
          </div>
        </div>
        {props.userType !== 'Sales Rep' && props.userType !== 'Office Staff' && (
          <div className="serial-numbers-body-add-new">
            <Typography variant="body1">Add New Serial Numbers</Typography>
            <div>
              <FormControl>
                <Autocomplete
                  options={[]}
                  value={Array.from(newSerialNumbers)}
                  inputValue={newSerialInput}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      label="Serial Numbers"
                      helperText="Separate each value with a comma or space"
                    />
                  )}
                  multiple
                  freeSolo
                  onInputChange={(_, value) => {
                    const options = value.split(/[,| ]/g)
                    if (options.length > 1) {
                      setNewSerialNumbers(
                        new Set(
                          Array.from(newSerialNumbers)
                            .concat(options)
                            .map(x => x.trim())
                            .filter(x => x),
                        ),
                      )
                      setNewSerialInput('')
                    } else {
                      setNewSerialInput(value)
                    }
                  }}
                  onChange={(_, value) => {
                    setNewSerialNumbers(new Set(value))
                  }}
                />
                {addError.show && (
                  <Alert severity="error">
                    {addError.duplicates
                      ? `Serial Number${addError.duplicates.length > 1 ? 's' : ''}: ${addError.duplicates.join(
                          ', ',
                        )} currently exist${addError.duplicates.length > 1 ? '' : 's'} and cannot be added again.`
                      : 'An error occurred while adding new serial numbers. Please try again or contact support if the error persists.'}
                  </Alert>
                )}
              </FormControl>
              <Button size="large" variant="contained" onClick={handleAdd} disabled={newSerialNumbers.size === 0}>
                Add
              </Button>
            </div>
          </div>
        )}
        <div className="serial-numbers-list-container">
          {props.userType !== 'Office Staff' && (
            <div className="serial-numbers-list-container-selected">
              <Typography variant="body1" color="textSecondary">
                {selectedSerialNumbers.length} Selected
              </Typography>
              <div className="serial-numbers-delete-button-container">
                <Button
                  variant="contained"
                  disabled={selectedSerialNumbers.length === 0}
                  startIcon={<DeleteOutlined />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
                {deleteError.show && (
                  <Alert severity="error">An error occurred while deleting serial numbers. Please try again.</Alert>
                )}
              </div>
            </div>
          )}
          <TableContainer className="serial-numbers-body-table">
            <Table>
              <TableHead className="serial-numbers-body-table-header">
                <TableRow>
                  <TableCell width="42px">
                    <Checkbox
                      checked={
                        props.serialNumbers.length > 0 && selectedSerialNumbers.length === props.serialNumbers.length
                      }
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedSerialNumbers(props.serialNumbers.map(s => s.serial_number))
                        } else {
                          setSelectedSerialNumbers([])
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Serial Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serialNumbers.map(serial => (
                  <TableRow key={serial}>
                    <TableCell>
                      <Checkbox
                        checked={selectedSerialNumbers.includes(serial)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedSerialNumbers([...selectedSerialNumbers, serial])
                          } else {
                            setSelectedSerialNumbers(selectedSerialNumbers.filter(s => s !== serial))
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{serial}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  )
}

export default SerialNumbers
